/**
 * WebGLBackground
 * 
 * Single Canvas containing:
 * 1. Full-viewport shader plane (simplex noise background)
 * 2. GPGPU particle terrain field
 * 3. EffectComposer post-processing (chromatic aberration, grain, vignette)
 * 
 * Position: fixed, z-index: -1, pointer-events: none.
 * All uniform updates happen inside useFrame via refs — zero re-renders.
 */

import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { EffectComposer, ChromaticAberration, Noise, Vignette } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import { useScrollVelocity } from '../../context/ScrollVelocityContext';
import GPGPUParticleField from './GPGPUParticleField';

// ─── VERTEX SHADER ───
const vertexShader = `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// ─── FRAGMENT SHADER (Ashima Arts 3D Simplex Noise inlined) ───
const fragmentShader = `
precision mediump float;

uniform float uTime;
uniform float uScrollVelocity;
uniform vec2 uResolution;

varying vec2 vUv;

vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 10.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
    i.z + vec4(0.0, i1.z, i2.z, 1.0))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0))
    + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  vec4 m = max(0.5 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 105.0 * dot(m * m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}

void main() {
  vec2 uv = vUv;
  float aspect = uResolution.x / uResolution.y;
  uv.x *= aspect;

  float time = uTime * 0.3;
  float vel = abs(uScrollVelocity);
  float turbulence = 1.0 + vel * 2.0;

  float n = 0.0;
  n += 0.5 * snoise(vec3(uv * 1.5 * turbulence, time));
  n += 0.25 * snoise(vec3(uv * 3.0 * turbulence, time * 1.3));
  n += 0.125 * snoise(vec3(uv * 6.0, time * 0.7));

  n = n * 0.5 + 0.5;

  // Clamp to #000000–#1a1a1a (0.0 to 0.102)
  float brightness = n * 0.102;

  gl_FragColor = vec4(vec3(brightness), 1.0);
}
`;

// ─── SHADER PLANE ───
const BackgroundPlane = () => {
    const meshRef = useRef();
    const velocityRef = useScrollVelocity();
    const { viewport } = useThree();
    const smoothedVelocity = useRef(0);

    const uniforms = useMemo(() => ({
        uTime: { value: 0 },
        uScrollVelocity: { value: 0 },
        uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
    }), []);

    useFrame((state, delta) => {
        if (!meshRef.current) return;
        const mat = meshRef.current.material;

        mat.uniforms.uTime.value += delta;

        const rawVelocity = Math.abs(velocityRef.current || 0);
        const normalizedVelocity = Math.min(rawVelocity / 3000, 1.0);

        // Exponential decay: 800ms
        const decayFactor = Math.exp(-delta / 0.8);
        smoothedVelocity.current = Math.max(
            normalizedVelocity,
            smoothedVelocity.current * decayFactor
        );

        mat.uniforms.uScrollVelocity.value = smoothedVelocity.current;
        mat.uniforms.uResolution.value.set(
            state.size.width * state.viewport.dpr,
            state.size.height * state.viewport.dpr
        );
    });

    return (
        <mesh ref={meshRef} scale={[viewport.width, viewport.height, 1]}>
            <planeGeometry args={[1, 1]} />
            <shaderMaterial
                vertexShader={vertexShader}
                fragmentShader={fragmentShader}
                uniforms={uniforms}
                depthWrite={false}
                depthTest={false}
            />
        </mesh>
    );
};

// ─── SCROLL-REACTIVE CHROMATIC ABERRATION ───
const ScrollChromaticAberration = () => {
    const caRef = useRef();
    const velocityRef = useScrollVelocity();
    const smoothedVelocity = useRef(0);
    const offsetVec = useMemo(() => new THREE.Vector2(0.0005, 0.0005), []);

    useFrame((_, delta) => {
        if (!caRef.current) return;
        const rawVelocity = Math.abs(velocityRef.current || 0);
        const normalizedVelocity = Math.min(rawVelocity / 3000, 1.0);

        // Exponential decay: 600ms
        const decayFactor = Math.exp(-delta / 0.6);
        smoothedVelocity.current = Math.max(
            normalizedVelocity,
            smoothedVelocity.current * decayFactor
        );

        const offsetValue = 0.0005 + smoothedVelocity.current * 0.0025;
        offsetVec.set(offsetValue, offsetValue);
        caRef.current.offset = offsetVec;
    });

    return (
        <ChromaticAberration
            ref={caRef}
            blendFunction={BlendFunction.NORMAL}
            offset={offsetVec}
        />
    );
};

// ─── SINGLE CANVAS WITH SCENE + PARTICLES + POST-PROCESSING ───
const WebGLBackground = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: -1,
                pointerEvents: 'none',
                opacity: 0.75,
            }}
        >
            <Canvas
                frameloop="always"
                dpr={isMobile ? [1, 1] : [1, 1.5]}
                gl={{
                    antialias: false,
                    alpha: false,
                    powerPreference: 'high-performance',
                    stencil: false,
                    depth: true,
                }}
                camera={{ position: [0, 2.5, 5], fov: 60 }}
                style={{ pointerEvents: 'none' }}
            >
                {/* Background shader plane — screen-space, behind everything */}
                <BackgroundPlane />

                {/* GPGPU Particle Terrain */}
                <GPGPUParticleField />

                {/* Post-processing effects — skipped on mobile to save GPU */}
                {!isMobile && (
                    <EffectComposer>
                        <ScrollChromaticAberration />
                        <Noise
                            premultiply={false}
                            blendFunction={BlendFunction.ADD}
                            opacity={0.04}
                        />
                        <Vignette
                            darkness={0.5}
                            offset={0.3}
                            blendFunction={BlendFunction.NORMAL}
                        />
                    </EffectComposer>
                )}
            </Canvas>
        </div>
    );
};

export default WebGLBackground;
